/**
 * NewRequestSplitButton
 * Material UI-style split button with default action (New Request)
 * and dropdown action (Create from File)
 *
 * When offline, displays a simple button since AI features require internet
 */

import { Plus, FileText, ChevronDown, Mic, Type } from 'lucide-react-native';
import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
} from 'react-native';

import { networkService } from '../../services/network';

// Conditional import for react-dom
let createPortal: any;
if (Platform.OS === 'web') {
  try {
    createPortal = require('react-dom').createPortal;
  } catch (e) {
    console.warn('react-dom not available');
  }
}

export interface NewRequestSplitButtonProps {
  onNewRequest: () => void;
  onImportFile: () => void;
  onImportAudio: () => void;
  onImportText?: () => void;
  disabled?: boolean;
  hasCreatePermission: boolean;
  /** Gate the Create-from-Text option by Button - Text Intake (default true). */
  canShowTextIntake?: boolean;
}

export const NewRequestSplitButton: React.FC<NewRequestSplitButtonProps> = ({
  onNewRequest,
  onImportFile,
  onImportAudio,
  onImportText = () => {},
  disabled = false,
  hasCreatePermission,
  canShowTextIntake = true,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const buttonRef = useRef<TouchableOpacity>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [isOnline, setIsOnline] = useState(networkService.isConnected());

  // Monitor network connection state
  useEffect(() => {
    const cleanup = networkService.addListener(connected => {
      setIsOnline(connected);
    });
    return cleanup;
  }, []);

  // Positioning function for dropdown
  const updateDropdownPosition = () => {
    if (Platform.OS === 'web' && buttonRef.current) {
      const element = buttonRef.current as any;
      const rect = element.getBoundingClientRect();
      const dropdownWidth = Math.max(rect.width, 280);

      // Always align dropdown to the right edge of the button
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - dropdownWidth,
        width: dropdownWidth,
      });
    }
  };

  // Close dropdown when clicking outside (web only)
  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !dropdownVisible ||
      typeof document === 'undefined'
    ) {
      return;
    }

    const handleClickOutside = (event: any) => {
      const portal = document.querySelector(
        '[data-new-request-dropdown-portal="true"]',
      );
      if (
        portal &&
        !portal.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };

    const handleScroll = () => {
      if (dropdownVisible) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [dropdownVisible]);

  if (!hasCreatePermission) {
    return null;
  }

  // When offline, show simple button since AI features require internet
  if (!isOnline) {
    return (
      <TouchableOpacity
        testID="new-request-button"
        style={[styles.simpleButton, disabled && styles.disabledButton]}
        onPress={onNewRequest}
        disabled={disabled}
        accessibilityLabel="Create new request"
        accessibilityRole="button"
        activeOpacity={0.8}
      >
        <Plus size={18} color="#FFFFFF" style={styles.icon} />
        <Text style={styles.simpleButtonText}>New Request</Text>
      </TouchableOpacity>
    );
  }

  const toggleDropdown = () => {
    if (!disabled) {
      if (!dropdownVisible && Platform.OS === 'web') {
        updateDropdownPosition();
      }
      setDropdownVisible(!dropdownVisible);
    }
  };

  const handleImportFile = () => {
    setDropdownVisible(false);
    onImportFile();
  };

  const handleImportAudio = () => {
    setDropdownVisible(false);
    onImportAudio();
  };

  const handleImportText = () => {
    setDropdownVisible(false);
    onImportText();
  };

  const handleNewRequest = () => {
    if (!disabled) {
      onNewRequest();
    }
  };

  if (Platform.OS === 'web') {
    return (
      <View style={styles.container} pointerEvents="box-none">
        {/* Split Button */}
        <View style={styles.splitButton} pointerEvents="box-none">
          {/* Main Action: New Request */}
          <TouchableOpacity
            testID="new-request-button"
            style={[styles.mainButton, disabled && styles.disabledButton]}
            onPress={handleNewRequest}
            disabled={disabled}
            accessibilityLabel="Create new request"
            accessibilityRole="button"
            activeOpacity={0.8}
          >
            <Plus size={18} color="#FFFFFF" style={styles.icon} />
            <Text style={styles.mainButtonText}>New Request</Text>
          </TouchableOpacity>

          {/* Divider */}
          <View style={styles.divider} pointerEvents="none" />

          {/* Dropdown Toggle */}
          <TouchableOpacity
            testID="new-request-dropdown-toggle"
            ref={buttonRef}
            style={[
              styles.dropdownToggle,
              disabled && styles.disabledButton,
              dropdownVisible && styles.dropdownToggleActive,
            ]}
            onPress={toggleDropdown}
            disabled={disabled}
            accessibilityLabel="Show create options"
            accessibilityRole="button"
            activeOpacity={0.8}
          >
            <ChevronDown size={18} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {/* Portal dropdown */}
        {dropdownVisible &&
          !disabled &&
          createPortal &&
          createPortal(
            <div
              data-new-request-dropdown-portal="true"
              style={{
                position: 'absolute',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                zIndex: 999999,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '8px 0' }}>
                {/* Option 1: Audio Import */}
                <div
                  data-testid="import-audio-option"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e: any) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e: any) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={handleImportAudio}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1C3A70"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: '12px' }}
                  >
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                  </svg>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333',
                        marginBottom: '2px',
                      }}
                    >
                      Create Request from Audio
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Upload audio recording for transcription
                    </div>
                  </div>
                </div>

                {/* Option 2: Document Import */}
                <div
                  data-testid="import-document-option"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e: any) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e: any) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={handleImportFile}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1C3A70"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: '12px' }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333',
                        marginBottom: '2px',
                      }}
                    >
                      Create Request from Document
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Upload a PDF, image, or text file for extraction
                    </div>
                  </div>
                </div>

                {/* Option 3: Text Import */}
                {canShowTextIntake && (
                  <div
                    data-testid="import-text-option"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                    }}
                    onMouseEnter={(e: any) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e: any) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={handleImportText}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1C3A70"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginRight: '12px' }}
                    >
                      <polyline points="4 7 4 4 20 4 20 7" />
                      <line x1="9" y1="20" x2="15" y2="20" />
                      <line x1="12" y1="4" x2="12" y2="20" />
                    </svg>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          color: '#333',
                          marginBottom: '2px',
                        }}
                      >
                        Create Request from Text
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Paste email or document text for extraction
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            typeof document !== 'undefined' ? document.body : (null as any),
          )}
      </View>
    );
  }

  // React Native Modal version
  return (
    <View style={styles.container}>
      {/* Split Button */}
      <View style={styles.splitButton}>
        {/* Main Action: New Request */}
        <TouchableOpacity
          style={[styles.mainButton, disabled && styles.disabledButton]}
          onPress={handleNewRequest}
          disabled={disabled}
          accessibilityLabel="Create new request"
          accessibilityRole="button"
        >
          <Plus size={18} color="#FFFFFF" style={styles.icon} />
          <Text style={styles.mainButtonText}>New Request</Text>
        </TouchableOpacity>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Dropdown Toggle */}
        <TouchableOpacity
          ref={buttonRef}
          style={[
            styles.dropdownToggle,
            disabled && styles.disabledButton,
            dropdownVisible && styles.dropdownToggleActive,
          ]}
          onPress={toggleDropdown}
          disabled={disabled}
          accessibilityLabel="Show import options"
          accessibilityRole="button"
        >
          <ChevronDown size={18} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Modal Dropdown */}
      <Modal
        visible={dropdownVisible && !disabled}
        transparent
        animationType="fade"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>New Request Options</Text>

            <TouchableOpacity
              testID="import-audio-option"
              style={styles.modalOption}
              onPress={handleImportAudio}
            >
              <Mic size={20} color="#1C3A70" style={styles.optionIcon} />
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>
                  Create Request from Audio
                </Text>
                <Text style={styles.optionDescription}>
                  Upload audio recording for transcription
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              testID="import-document-option"
              style={styles.modalOption}
              onPress={handleImportFile}
            >
              <FileText size={20} color="#1C3A70" style={styles.optionIcon} />
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>
                  Create Request from Document
                </Text>
                <Text style={styles.optionDescription}>
                  Upload a PDF, image, or text file for extraction
                </Text>
              </View>
            </TouchableOpacity>

            {canShowTextIntake && (
              <TouchableOpacity
                testID="import-text-option"
                style={styles.modalOption}
                onPress={handleImportText}
              >
                <Type size={20} color="#1C3A70" style={styles.optionIcon} />
                <View style={styles.optionTextContainer}>
                  <Text style={styles.optionTitle}>
                    Create Request from Text
                  </Text>
                  <Text style={styles.optionDescription}>
                    Paste email or document text for extraction
                  </Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 1000,
  },
  simpleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#FF6B35',
    borderRadius: 6,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'background-color 0.2s' as any,
      },
    }),
  },
  simpleButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  splitButton: {
    flexDirection: 'row',
    backgroundColor: 'transparent',
    borderRadius: 6,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  mainButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#FF6B35',
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'background-color 0.2s' as any,
      },
    }),
  },
  icon: {
    marginRight: 8,
  },
  mainButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  divider: {
    width: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  dropdownToggle: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 12,
    backgroundColor: '#FF6B35',
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'background-color 0.2s' as any,
      },
    }),
  },
  dropdownToggleActive: {
    backgroundColor: '#E55A25',
  },
  disabledButton: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    paddingVertical: 20,
    marginHorizontal: 20,
    minWidth: 320,
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginBottom: 20,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  optionIcon: {
    marginRight: 12,
  },
  optionTextContainer: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
    color: '#666',
  },
});
